import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, BadgePercent, Zap, PiggyBank } from "lucide-react";
import { useT } from "@/i18n";
import { fmtPrice } from "@/lib/content";
import OrderDialog from "./OrderDialog";
import PackageQuiz from "./PackageQuiz";
import PackageCompare from "./PackageCompare";
import BillingCarousel from "./BillingCarousel";

const toNum = (v) => parseFloat(String(v ?? "").replace(/\s/g, "").replace(",", "."));

const BILLINGS = [
  { id: "monthly", field: "price_monthly", total: null, old: "old_price_monthly", oldTotal: null, testid: "billing-monthly-button" },
  { id: "3", field: "price_3", total: "total_3", old: "old_price_3", oldTotal: "old_total_3", testid: "billing-3m-button" },
  { id: "6", field: "price_6", total: "total_6", old: "old_price_6", oldTotal: "old_total_6", testid: "billing-6m-button" },
  { id: "12", field: "price_annual", total: "total_12", old: "old_price_annual", oldTotal: "old_total_12", testid: "billing-annual-button" },
  { id: "24", field: "price_24", total: "total_24", old: "old_price_24", oldTotal: "old_total_24", testid: "billing-24m-button" },
];
const BILLING_MONTHS = { 3: 3, 6: 6, 12: 12, 24: 24 };

export function PromoBanner({ promo }) {
  if (!promo?.enabled || !promo.title) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6 }}
      data-testid="pricing-promo-banner"
      className="mt-8 relative overflow-hidden rounded-2xl bg-brand-red text-white px-6 py-5 sm:px-8 sm:py-6 flex items-start gap-4 shadow-lg shadow-brand-red/25"
    >
      <span aria-hidden="true" className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-brand-yellow/30 blur-2xl" />
      <span className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center shrink-0"><BadgePercent size={22} /></span>
      <div className="relative">
        <p data-testid="pricing-promo-title" className="font-display font-extrabold text-lg sm:text-xl tracking-tight">{promo.title}</p>
        {promo.text && <p data-testid="pricing-promo-text" className="text-sm text-white/90 mt-1 leading-relaxed whitespace-pre-line">{promo.text}</p>}
      </div>
    </motion.div>
  );
}

export default function Pricing({ packages, promo: sitePromo, show24 = false, note24 = "", comparison = null }) {
  const { t, lang } = useT();
  const [billing, setBilling] = useState(show24 ? "24" : "12");
  const touched = useRef(false);
  useEffect(() => { if (show24 && !touched.current) setBilling("24"); }, [show24]);
  const [order, setOrder] = useState(null);
  const source = useRef("pricing");
  const openOrder = (id, src) => { source.current = src; setOrder(id); };
  const [quiz, setQuiz] = useState(false);
  const billings = BILLINGS.filter((b) => b.id !== "24" || show24);
  useEffect(() => {
    const h = (e) => openOrder(e.detail || packages[0]?.id || null, e.detail ? "chat" : "text");
    window.addEventListener("tb:open-order", h);
    const pending = sessionStorage.getItem("tb:pending-order");
    if (pending) {
      sessionStorage.removeItem("tb:pending-order");
      const src = sessionStorage.getItem("tb:order-source") || "link";
      sessionStorage.removeItem("tb:order-source");
      const id = packages.some((p) => p.id === pending) ? pending : packages[0]?.id || null;
      const pb = sessionStorage.getItem("tb:pending-billing");
      if (pb) {
        sessionStorage.removeItem("tb:pending-billing");
        touched.current = true;
        setBilling(pb);
      }
      setTimeout(() => openOrder(id, src), 600);
    }
    return () => window.removeEventListener("tb:open-order", h);
  }, [packages]);
  const cfg = billings.find((b) => b.id === billing) || billings[0];
  const billingLabels = { monthly: t.pricing.monthly, 3: t.pricing.m3, 6: t.pricing.m6, 12: t.pricing.annual, 24: t.pricing.m24 };

  return (
    <section id="biuro-wirtualne-cennik" data-testid="pricing-section" className="py-16 sm:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.25em] text-slate-600 dark:text-slate-400 font-semibold"
            >
              <span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-yellow mr-3 align-middle" />{t.pricing.overline}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-4"
            >
              {t.pricing.heading}
            </motion.h2>
          </div>

          <div className="w-full mt-2">
            <BillingCarousel
              items={billings}
              value={billing}
              labels={billingLabels}
              shortUnit={t.pricing.months_short}
              ariaPrev={t.pricing.prev_period}
              ariaNext={t.pricing.next_period}
              onChange={(id) => { touched.current = true; setBilling(id); }}
            />
          </div>
        </div>

        <motion.button
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          data-testid="quiz-open-button"
          onClick={() => setQuiz(true)}
          className="mt-10 group inline-flex items-center gap-3 rounded-full border border-slate-300 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 backdrop-blur px-5 py-3 text-sm font-semibold hover:border-slate-500 hover:bg-white dark:hover:bg-slate-900 transition-colors"
        >
          <span className="w-8 h-8 rounded-full bg-brand-yellow text-brand-navy flex items-center justify-center group-hover:rotate-12 transition-transform">
            <Sparkles size={15} />
          </span>
          {t.quiz.cta}
        </motion.button>

        <PromoBanner promo={sitePromo} />

        {billing === "24" && show24 && note24 && (
          <motion.p
            key="note24"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="pricing-24-note"
            className="mt-8 rounded-2xl border border-brand-teal/40 bg-brand-teal/10 text-brand-tealDark dark:text-brand-teal px-5 py-4 text-sm font-semibold leading-relaxed whitespace-pre-line"
          >
            {note24}
          </motion.p>
        )}

        <div className={`mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 ${packages.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
          {packages.map((p, i) => {
            const tr = (t.pricing.pkgs && t.pricing.pkgs[p.id]) || {};
            const name = lang === "pl" ? p.name : tr.name || p.name;
            const badge = lang === "pl" ? p.badge : tr.badge || "";
            const tagline = lang === "pl" ? p.tagline : (p.tagline ? tr.tagline || p.tagline : "");
            const note = lang === "pl" ? p.note : tr.note || "";
            const features = lang === "pl" ? p.features : tr.features || p.features;
            const price = p[cfg.field] || "—";
            const total = cfg.total ? p[cfg.total] : null;
            const popular = p.id === "allinclusive";
            const promo = p.promo?.enabled ? p.promo : null;
            const oldPrice = promo && promo[cfg.old] && promo[cfg.old] !== price ? promo[cfg.old] : null;
            const oldTotal = promo && cfg.oldTotal && promo[cfg.oldTotal] && promo[cfg.oldTotal] !== total ? promo[cfg.oldTotal] : null;
            const muted = popular ? "text-slate-300" : "text-slate-500 dark:text-slate-400";
            const yearlySavings = popular && total ? Math.round((toNum(p.price_monthly) - toNum(price)) * 12) : 0;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.1, duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`pricing-card-${p.id}`}
                className={`relative flex flex-col rounded-2xl border p-6 sm:p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  popular
                    ? "bg-brand-navy text-white border-brand-navy dark:border-brand-teal/50 shadow-xl shadow-brand-navy/20"
                    : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-brand-teal/40"
                } ${promo ? "ring-2 ring-brand-red ring-offset-2 ring-offset-white dark:ring-offset-slate-950" : ""}`}
              >
                {promo && (
                  <span data-testid={`pricing-promo-tag-${p.id}`} className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-full bg-brand-red text-white shadow-md shadow-brand-red/30 ${badge && popular ? "self-start mb-3 mt-2" : "absolute -top-3 right-5"}`}>
                    <BadgePercent size={11} /> {promo.label || t.pricing.promo_tag}
                  </span>
                )}
                {badge && popular && (
                  <span className={`absolute -top-3 left-6 font-mono text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1 rounded-full text-white ${promo ? "bg-brand-navy border border-white/30" : "bg-brand-red"}`}>
                    {badge}
                  </span>
                )}
                {badge && !popular && (
                  <span className="self-start mb-3 font-mono text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                    {badge}
                  </span>
                )}
                <h3 className="font-display font-bold text-xl tracking-tight">{name}</h3>
                {tagline && (
                  <p data-testid={`pricing-tagline-${p.id}`} className={`mt-1 font-display font-extrabold text-lg tracking-tight ${popular ? "text-brand-yellow" : "text-brand-teal"}`}>{tagline}</p>
                )}
                {oldPrice && (
                  <p data-testid={`pricing-old-price-${p.id}`} className={`mt-4 -mb-3 text-sm font-mono flex flex-wrap items-baseline gap-x-2 ${muted}`}>
                    <s className="decoration-brand-red decoration-2 whitespace-nowrap">{fmtPrice(oldPrice)} {t.pricing.per_month}</s>
                    <span className="text-[10px] uppercase tracking-wider whitespace-nowrap">{t.pricing.promo_regular}</span>
                  </p>
                )}
                <div className="mt-5 flex items-baseline gap-1.5 flex-wrap">
                  <span className={`font-display font-extrabold text-3xl sm:text-4xl tracking-tight ${promo ? (popular ? "text-brand-yellow" : "text-brand-red") : ""}`}>{fmtPrice(price)}</span>
                  <span className={`text-sm ${muted}`}>{t.pricing.per_month}</span>
                </div>
                {total && (
                  <>
                    <p data-testid={`pricing-upfront-note-${p.id}`} className={`text-xs mt-2 font-semibold ${popular ? "text-brand-yellow/90" : "text-brand-tealDark dark:text-brand-teal"}`}>
                      {t.pricing.upfront_note.replace("{m}", String(BILLING_MONTHS[billing]))}
                    </p>
                    <p className={`text-xs mt-1 font-mono ${muted}`}>
                      {t.pricing.upfront}: {oldTotal && <s data-testid={`pricing-old-total-${p.id}`} className="mr-1.5 decoration-brand-red">{fmtPrice(oldTotal)}</s>}{fmtPrice(total)} {t.pricing.net_unit}
                    </p>
                    {yearlySavings > 0 && (
                      <p data-testid={`pricing-savings-${p.id}`} className="mt-3 flex items-start gap-2 rounded-xl bg-brand-yellow text-brand-navy text-xs font-bold px-3 py-2 leading-snug">
                        <PiggyBank size={15} className="shrink-0 mt-px" /> <span>{t.pricing.savings.replace("{x}", fmtPrice(yearlySavings))}</span>
                      </p>
                    )}
                  </>
                )}
                {promo?.description && (
                  <p data-testid={`pricing-promo-desc-${p.id}`} className={`mt-3 text-xs font-semibold leading-snug rounded-lg px-3 py-2 ${popular ? "bg-white/10 text-brand-yellow" : "bg-brand-red/10 text-brand-red dark:bg-brand-red/15"}`}>
                    {promo.description}
                  </p>
                )}
                {note && (
                  <p className={`text-xs mt-2 ${muted}`}>{note}</p>
                )}
                <ul className="mt-6 space-y-3 flex-1">
                  {features.map((f, fi) => {
                    const hl = f.startsWith("!");
                    const text = hl ? f.slice(1).trim() : f;
                    if (hl) {
                      return (
                        <li key={fi} data-testid={`pricing-feature-highlight-${p.id}`} className={`flex items-start gap-2.5 text-sm rounded-lg px-2.5 py-2 -mx-2.5 ${popular ? "bg-brand-yellow/15 ring-1 ring-brand-yellow/50" : "bg-brand-teal/10 ring-1 ring-brand-teal/40"}`}>
                          <Zap size={15} className={`mt-0.5 shrink-0 ${popular ? "text-brand-yellow fill-brand-yellow" : "text-brand-teal fill-brand-teal"}`} />
                          <span className={`flex-1 font-bold ${popular ? "text-brand-yellow" : "text-brand-tealDark dark:text-brand-teal"}`}>{text}</span>
                        </li>
                      );
                    }
                    return (
                      <li key={fi} className="flex items-start gap-2.5 text-sm">
                        <Check size={15} className={`mt-0.5 shrink-0 ${popular ? "text-brand-yellow" : "text-brand-teal"}`} />
                        <span className={popular ? "text-slate-200" : "text-slate-600 dark:text-slate-300"}>{f}</span>
                      </li>
                    );
                  })}
                </ul>
                <button
                  data-testid={`pricing-select-${p.id}`}
                  onClick={() => openOrder(p.id, "pricing")}
                  className={`mt-8 w-full py-3 rounded-full text-sm font-semibold transition-colors duration-200 ${
                    popular
                      ? "bg-brand-yellow text-brand-navy hover:bg-white"
                      : "bg-brand-teal text-white hover:bg-brand-tealDark"
                  }`}
                >
                  {t.order.cta} {name}
                </button>
              </motion.div>
            );
          })}
        </div>

        {comparison?.enabled !== false && comparison?.rows?.length > 0 && (
          <PackageCompare packages={packages} rows={comparison.rows} cfg={cfg} onOrder={(id) => openOrder(id, "compare")} />
        )}

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          data-testid="accounting-pricing"
          className="mt-16 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8 lg:p-10"
        >
          <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight">{t.pricing.acc_title}</h3>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-12">
            {t.pricing.acc_rows.map((a) => (
              <div
                key={a.name}
                className="flex items-baseline justify-between gap-4 py-3 border-b border-dashed border-slate-200 dark:border-slate-700 text-sm"
              >
                <span className="text-slate-600 dark:text-slate-300">{a.name}</span>
                <span className="font-mono font-semibold whitespace-nowrap">{a.price}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-5">{t.pricing.acc_note}</p>
        </motion.div>
      </div>
      <PackageQuiz open={quiz} onOpenChange={setQuiz} packages={packages} onOrder={(id) => openOrder(id, "quiz")} />
      <OrderDialog
        open={order !== null}
        onOpenChange={(o) => !o && setOrder(null)}
        packages={packages}
        initialPackage={order || packages[0]?.id}
        initialBilling={billing}
        show24={show24}
        source={source.current}
      />
    </section>
  );
}
